import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { BookOpen, ChevronLeft, ChevronRight, Search } from "lucide-react";
import { ReportCorrection } from "@/components/ReportCorrection";
import Seo from "@/components/Seo";
import { CommentaryAccessNotice, isAccessError } from "@/components/commentary/CommentaryAccess";
import { CommentaryNoNotes } from "@/components/commentary/CommentaryNoNotes";
import { BackToTop, scrollToEl } from "@/components/ChapterNav";
import { useCommentaries, useCommentaryChapter, ChapterIntro } from "@/components/commentary/CommentaryPanel";
import { CommentaryBreadcrumb } from "@/components/commentary/CommentaryBreadcrumb";
import { BackToSearch } from "@/components/search/BackToSearch";
import { CommentarySection } from "@/components/commentary/CommentarySection";
import { useHighlights } from "@/components/highlights/useHighlights";
import { MarkTermsCtx, useFlashHighlight } from "@/components/highlights/HighlightScope";

function ChapterStrip({ slug, book, current, chapters }) {
  const stripRef = useRef(null);
  useEffect(() => {
    const strip = stripRef.current, chip = strip?.querySelector(`[data-chapter="${current}"]`);
    if (strip && chip) strip.scrollTo({ left: chip.offsetLeft - strip.clientWidth / 2 + chip.clientWidth / 2, behavior: "smooth" });
  }, [current, chapters]);
  if (!chapters || chapters.length < 2) return null;
  return (
    <nav className="sticky top-16 z-30 -mx-4 mt-6 border-b border-border bg-background/85 px-4 py-3 glass sm:mx-0 sm:rounded-2xl sm:border sm:px-4" aria-label="Jump to chapter" data-testid="commentary-chapter-nav">
      <div className="flex items-center gap-3">
        <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Chapter</span>
        <div ref={stripRef} className="flex flex-1 gap-1.5 overflow-x-auto pb-1 [scrollbar-width:thin]" data-lenis-prevent>
          {chapters.map((c) => (
            <Link key={c} to={`/commentary/${slug}/${encodeURIComponent(book)}/${c}`} data-chapter={c} aria-current={c === current ? "page" : undefined}
              className={`shrink-0 rounded-full px-3 py-1 font-mono text-xs transition-colors ${c === current ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:bg-muted hover:text-foreground"}`}
              data-testid={`commentary-chapter-nav-${c}`}>{c}</Link>
          ))}
        </div>
      </div>
    </nav>
  );
}

const shortRef = (s) => s.reference.replace(/^.*?\s(?=\d+[:—]|\d+$)/, "");

/* Second row: verse-range chips that scroll to each section of this chapter. */
function SectionStrip({ sections, activeId }) {
  const stripRef = useRef(null);
  const [active, setActive] = useState(activeId);
  useEffect(() => { setActive(activeId); }, [activeId]);
  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      const hit = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
      if (hit) setActive(hit.target.id);
    }, { rootMargin: "-200px 0px -55% 0px", threshold: 0 });
    sections.forEach((s) => { const el = document.getElementById(s.id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [sections]);
  useEffect(() => {
    const strip = stripRef.current, chip = strip?.querySelector(`[data-section="${CSS.escape(String(active))}"]`);
    if (strip && chip) strip.scrollTo({ left: chip.offsetLeft - strip.clientWidth / 2 + chip.clientWidth / 2, behavior: "smooth" });
  }, [active]);
  if (sections.length < 2) return null;
  return (
    <div className="mt-2 flex items-center gap-3 px-1" data-testid="commentary-section-nav">
      <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Passage</span>
      <div ref={stripRef} className="flex flex-1 gap-1.5 overflow-x-auto pb-1 [scrollbar-width:thin]" data-lenis-prevent>
        {sections.map((s) => (
          <button key={s.id} type="button" data-section={s.id} title={s.title || s.reference} onClick={() => scrollToEl(document.getElementById(s.id)?.closest("[data-hl-scope]"), -150)}
            className={`shrink-0 rounded-full border px-3 py-1 font-mono text-xs transition-colors ${active === s.id ? "border-gold bg-gold/20 text-foreground" : "border-border bg-card text-muted-foreground hover:border-gold hover:text-foreground"}`}
            data-testid={`commentary-section-nav-${s.id}`}>{shortRef(s)}</button>
        ))}
      </div>
    </div>
  );
}

/* /commentary/:slug/:book/:chapter — full-page commentary reading view */
export default function CommentaryChapter() {
  const { slug, book, chapter } = useParams();
  const [sp, setSp] = useSearchParams();
  const { hash } = useLocation();
  const navigate = useNavigate();
  const ch = Number(chapter) || 1;
  const markParam = sp.get("mark") || "";
  const marks = markParam.split(/\s+/).filter((t) => t.length >= 2 && !/^\d+$/.test(t));
  const clearMarks = () => setSp((prev) => { const n = new URLSearchParams(prev); n.delete("mark"); return n; }, { replace: true });
  const { data, isLoading, isError, error } = useCommentaryChapter(slug, book, ch);
  const { data: list } = useCommentaries();
  const current = list?.find((c) => c.slug === slug);
  const name = current?.name || "Commentary";
  const hl = useHighlights(book, ch);
  useFlashHighlight(sp.get("hl"), !!data && hl.all.length > 0);
  useEffect(() => {
    if (!data || !hash) { window.scrollTo({ top: 0 }); return; }
    setTimeout(() => {
      const section = document.getElementById(hash.slice(1));
      const firstMark = section?.closest("[data-hl-scope]")?.querySelector("[data-search-mark]");
      (firstMark || section)?.scrollIntoView({ block: firstMark ? "center" : "start", behavior: "smooth" });
    }, 100);
  }, [data, hash]);
  const go = (t) => t && navigate(`/commentary/${slug}/${encodeURIComponent(t.book)}/${t.chapter}`);
  const activeId = hash ? hash.slice(1) : null;

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8" data-testid="commentary-chapter">
      <Seo title={`${book} ${ch} — ${name}`} description={data?.intro[0]?.slice(0, 160) || `${name} on ${book} ${ch}`} />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <BackToSearch className="mb-1" />
          <CommentaryBreadcrumb slug={slug} book={book} chapter={ch} />
          {current && <p className="mt-2 font-serif text-lg italic text-gold" data-testid="commentary-full-name">{current.display_name}</p>}
          <h1 className="mt-1 font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl" data-testid="commentary-chapter-title">{book} {ch}</h1>
        </div>
        <div className="flex items-center gap-1">
          <Link to={`/commentary/${slug}/search`} className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-muted-foreground hover:border-gold hover:text-foreground" title="Search" data-testid="commentary-chapter-search"><Search className="h-4 w-4" /></Link>
          <Link to={`/read/${encodeURIComponent(book)}/${ch}?commentary=${slug}`} className="inline-flex h-9 items-center gap-1.5 rounded-full border border-border px-3 text-sm text-muted-foreground hover:border-gold hover:text-foreground" data-testid="commentary-chapter-reader"><BookOpen className="h-4 w-4 text-gold" /> Read with the Bible</Link>
          <button type="button" disabled={!data?.prev} onClick={() => go(data?.prev)} aria-label="Previous chapter" data-testid="commentary-prev" className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-muted-foreground hover:border-gold disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
          <button type="button" disabled={!data?.next} onClick={() => go(data?.next)} aria-label="Next chapter" data-testid="commentary-next" className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-muted-foreground hover:border-gold disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>
      <ChapterStrip slug={slug} book={book} current={ch} chapters={data?.chapters} />
      {data && <SectionStrip sections={data.sections} activeId={activeId} />}
      {marks.length > 0 && (
        <div className="mt-6 inline-flex flex-wrap items-center gap-2 rounded-full border border-gold/50 bg-gold/10 py-1.5 pl-4 pr-1.5 text-sm text-foreground" data-testid="search-marks-pill">
          <span>Showing search matches for <strong>“{marks.join(" ")}”</strong></span>
          <button type="button" onClick={clearMarks} data-testid="search-marks-clear" className="rounded-full bg-card px-3 py-1 text-xs font-semibold text-foreground hover:bg-muted">Clear</button>
        </div>
      )}
      <MarkTermsCtx.Provider value={marks}>
      <div className="mt-6 space-y-4">
        {isLoading && Array.from({ length: 4 }).map((_, i) => <div key={`skeleton-${i}`} className="h-24 animate-pulse rounded-2xl bg-muted" />)}
        {isError && (isAccessError(error) ? <CommentaryAccessNotice error={error} /> : <p className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">Something went wrong loading {name} on {book} {ch}.</p>)}
        {data && !data.has_notes && <CommentaryNoNotes name={name} book={book} chapter={ch} prev={data.prev} next={data.next} onGo={go} />}
        {data?.has_notes && <ChapterIntro paragraphs={data.intro} defaultOpen />}
        {data?.sections.map((s) => (
          <CommentarySection key={s.id} s={s} slug={slug} name={name} actions={hl} highlights={hl.all.filter((h) => h.section_id === s.id)} active={s.id === activeId}
            onJump={(v) => navigate(`/read/${encodeURIComponent(book)}/${ch}?v=${v}&commentary=${slug}`)} />
        ))}
      </div>
      </MarkTermsCtx.Provider>
      {data?.has_notes && <div className="mt-6 flex justify-end"><ReportCorrection reference={`${name} commentary · ${book} ${ch}`} testId="commentary-report-correction" /></div>}
      {data && (
        <div className="mt-8 flex items-center justify-between font-mono text-xs uppercase tracking-wider text-muted-foreground">
          <button type="button" disabled={!data.prev} onClick={() => go(data.prev)} className="inline-flex items-center gap-1 hover:text-foreground disabled:opacity-30" data-testid="commentary-prev-bottom"><ChevronLeft className="h-3.5 w-3.5" /> {data.prev ? `${data.prev.book} ${data.prev.chapter}` : ""}</button>
          <button type="button" disabled={!data.next} onClick={() => go(data.next)} className="inline-flex items-center gap-1 hover:text-foreground disabled:opacity-30" data-testid="commentary-next-bottom">{data.next ? `${data.next.book} ${data.next.chapter}` : ""} <ChevronRight className="h-3.5 w-3.5" /></button>
        </div>
      )}
      <BackToTop />
    </div>
  );
}
